import { Box, Divider, Drawer, Stack } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CheckboxController from '@/components/inputs/CheckboxController'
import { ConfigField } from '@/features/controller/configuration/components/drawers/ConfigField'
import { DrawerActions } from '@/features/controller/configuration/components/drawers/DrawerActions'
import { DrawerDescription } from '@/features/controller/configuration/components/drawers/DrawerDescription'
import { DrawerTitle } from '@/features/controller/configuration/components/drawers/DrawerTitle'
import type { TillquistConfig } from '@/features/controller/configuration/models/raspiProperties/types'
import type { ConfigDrawerProps } from '@/features/controller/configuration/types'

export function TillquistPowerMeterDrawer({
  config,
  onClose,
  onConfigUpdated,
  onFinishedClosing,
  open,
}: Readonly<ConfigDrawerProps<TillquistConfig>>) {
  const { t } = useTranslation()
  const form = useForm<TillquistConfig>({
    defaultValues: config,
  })

  const updatedTillquistConfig = {
    ...config,
    ...form.watch(),
  }

  const hasChanges = JSON.stringify(updatedTillquistConfig) !== JSON.stringify(config)

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 580 },
        },
      }}
      onClose={onClose}
      onTransitionExited={onFinishedClosing}
    >
      <FormProvider {...form}>
        <DrawerTitle title={t('controllers.configuration.power_meters.tillquist.dialog.title')} onClose={onClose} />

        <Divider />

        <DrawerDescription description={t('controllers.configuration.power_meters.tillquist.dialog.description')} />

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <Stack direction="column" padding={2} spacing={2}>
            <ConfigField label={t('controllers.configuration.power_meters.tillquist.dialog.power')}>
              <CheckboxController
                label={t('controllers.configuration.power_meters.tillquist.dialog.power_label')}
                name="powerEnabled"
              />
            </ConfigField>
            <ConfigField
              helpTooltip={t('controllers.configuration.power_meters.tillquist.dialog.frequency_hint')}
              label={t('controllers.configuration.power_meters.tillquist.dialog.frequency')}
            >
              <CheckboxController
                label={t('controllers.configuration.power_meters.tillquist.dialog.frequency_label')}
                name="frequencyEnabled"
              />
            </ConfigField>
          </Stack>
        </Box>

        <Divider />

        {/* Footer actions */}
        <DrawerActions
          hasChanges={hasChanges}
          onClose={onClose}
          onSave={(updatedConfig: TillquistConfig) => onConfigUpdated(updatedConfig)}
        />
      </FormProvider>
    </Drawer>
  )
}
